import React from "react";
import { RouteComponentProps } from "react-router-dom";
import "./styles.scss";
import {
  CloseButtonIcon,
  DesktopPopupModal,
  Icon,
  IconName,
  MobilePopoverCard,
  CaponeCorpLogo,
  CorporateTypography,
} from "halifax";
import clsx from "clsx";
import { getConfigTenant, isCorpTenant } from "@capone/common";
import { Box, Typography } from "@material-ui/core";
import { TRIP_BUILDER, HOTELS_FLIGHTS } from "@capone/experiments";
import {
  ANCILLARY_DISCLAIMER_TEXT,
  getCorporateAncillaryDisclaimerText,
  CORPORATE_PACKAGES_INFO_MODAL_HEADING_TEXT,
  CORPORATE_PACKAGES_INFO_MODAL_HEADING_TEXT_TRIP_BUILDER,
  CORPORATE_PACKAGES_INFO_MODAL_HEADING_TEXT_HOTELS_FLIGHTS,
  CORPORATE_PACKAGES_INFO_MODAL_POINTS,
  PACKAGES_INFO_MODAL_HEADING_TEXT,
  PACKAGES_INFO_MODAL_POINTS,
  PACKAGES_INFO_MODAL_POINTS_HEADING,
  PACKAGES_INFO_MODAL_SUBHEADING_TEXT,
  CORPORATE_PACKAGES_INFO_MODAL_SUBHEADING_TEXT,
} from "./textConstants";
import { PackagesInfoModalConnectorProps } from "./container";

export interface IPackagesInfoModalProps
  extends PackagesInfoModalConnectorProps,
  RouteComponentProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  isMobile?: boolean;
  corpPackagesNameVariant?: string;
}

export const PackagesInfoModal = ({
  open,
  setOpen,
  isMobile,
  corpPackagesNameVariant,
}: IPackagesInfoModalProps) => {
  const isCorporate = isCorpTenant(getConfigTenant());
  const modalInfoPoints = isCorporate
    ? CORPORATE_PACKAGES_INFO_MODAL_POINTS
    : PACKAGES_INFO_MODAL_POINTS;

  const getModalHeadingText = () => {
    if (!isCorporate) {
      return PACKAGES_INFO_MODAL_HEADING_TEXT;
    }

    switch (corpPackagesNameVariant) {
      case TRIP_BUILDER:
        return CORPORATE_PACKAGES_INFO_MODAL_HEADING_TEXT_TRIP_BUILDER;
      case HOTELS_FLIGHTS:
        return CORPORATE_PACKAGES_INFO_MODAL_HEADING_TEXT_HOTELS_FLIGHTS;
      default:
        return CORPORATE_PACKAGES_INFO_MODAL_HEADING_TEXT;
    }
  };

  const getDisclaimerText = () => {
    if (!isCorporate) {
      return ANCILLARY_DISCLAIMER_TEXT;
    }

    const packageName = getModalHeadingText();
    return getCorporateAncillaryDisclaimerText(packageName);
  };

  const ModalContent = React.useCallback(
    () => (
      <Box className="packages-info-modal-content">
        <Box
          className={clsx("packages-info-modal-header-content", {
            corporate: isCorporate,
          })}
        >
          {isCorporate ? (
            <img
              className="menu-logo"
              src={CaponeCorpLogo}
              alt="Capital One Business Travel"
            />
          ) : (
            <Icon
              name={
                isCorporate
                  ? IconName.CorpTravelLogoWhite
                  : IconName.CaponeTravelWhiteText
              }
            />
          )}
          <Box className={clsx("new-tag", { corporate: isCorporate })}>New</Box>
          <Box className="packages-info-modal-heading-container">
            <Box className="packages-info-modal-heading-icons">
              <Icon name={IconName.HotelFunnelIcon} />
              <Typography className="packages-icon-separator">+</Typography>
              <Icon name={IconName.FlightFunnelIcon} />
            </Box>
            {isCorporate ? (
              <CorporateTypography
                variant="h1"
                className="packages-info-modal-heading-text"
              >
                {getModalHeadingText()}
              </CorporateTypography>
            ) : (
              <Typography
                variant="h3"
                className="packages-info-modal-heading-text"
              >
                {getModalHeadingText()}
              </Typography>
            )}
          </Box>
          {isCorporate ? (
            <CorporateTypography
              variant="subtitle2"
              color="textPrimary"
              fontWeight="semibold"
              className="packages-info-modal-subheading-text"
            >
              {CORPORATE_PACKAGES_INFO_MODAL_SUBHEADING_TEXT}
            </CorporateTypography>
          ) : (
            <Typography className="packages-info-modal-subheading-text">
              {PACKAGES_INFO_MODAL_SUBHEADING_TEXT}
            </Typography>
          )}
        </Box>
        <Box className="packages-info-modal-body-content">
          {isMobile && (
            <Typography className="packages-info-point-heading" variant="h4">
              {PACKAGES_INFO_MODAL_POINTS_HEADING}
            </Typography>
          )}

          {modalInfoPoints.map(({ icon, text }) => (
            <Box className="packages-info-point">
              <Icon
                name={icon}
                className={clsx("packages-info-point-icon", {
                  corporate: isCorporate,
                })}
              />
              {isCorporate ? (
                <CorporateTypography
                  variant="body1"
                  color="textPrimary"
                  className="packages-info-point-text"
                >
                  {text}
                </CorporateTypography>
              ) : (
                <Typography className="packages-info-point-text">
                  {text}
                </Typography>
              )}
            </Box>
          ))}

          <hr />

          <Typography className="ancillary-disclaimer-text">
            {getDisclaimerText()}
          </Typography>
        </Box>
      </Box>
    ),
    [isCorporate, corpPackagesNameVariant]
  );

  if (isMobile)
    return (
      <MobilePopoverCard
        className="packages-info-mobile-popover"
        open={open}
        onClose={() => setOpen(false)}
        topRightButton={<CloseButtonIcon onClick={() => setOpen(false)} />}
      >
        <ModalContent />
      </MobilePopoverCard>
    );

  return (
    <DesktopPopupModal
      open={open}
      className="packages-info-modal"
      onClose={() => setOpen(false)}
      invisibleBackdrop={false}
    >
      <ModalContent />
    </DesktopPopupModal>
  );
};
